/**
 * parseItemsFromToolResult — turn a tool's `result.data` into the
 * normalised `FounsiItem[]` the chat surface renders as cards.
 *
 * Shapes verified against live `POST /assistant/chat` (USER_A, prod
 * backend, 2026-06-22).  See the inline shape comment per tool.
 *
 * For v1 we render cards only for tools that return navigable
 * inventory items (`inventory_item_id` set).  Other tools' results
 * still show as a tool-event chip — just no card row.
 *
 * Per the audit on frontend#280 we explicitly skip the
 * `image_descriptions` collection that `search_inventory` returns
 * alongside `user_inventory`: those are RAG retrieval hits with no
 * inventory id and would render as unclickable photo tiles that
 * confuse the user.  We dedupe by `inventory_item_id` to collapse
 * the multi-row-per-item bursts the vector search produces.
 */

export interface FounsiItem {
  /** Navigation target for ItemDetail.  Required — we only surface
   *  items we can open. */
  inventory_item_id: number;
  item_name: string;
  /** Best-effort sub-label for the card. */
  sub_label?: string;
  /** Signed GCS URL.  Passed to ItemThumbnail.source_image_url. */
  source_image_url?: string;
  /** Similarity score from the vector search; used only for sorting. */
  score?: number;
}

// ─── Per-tool shape adapters ────────────────────────────────────────

/**
 * `search_inventory.result.data` shape (verified live, 2026-06-22):
 * {
 *   items: Array<{
 *     score: number;
 *     owner_user_id: number;
 *     collection: 'user_inventory' | 'image_descriptions';  // <- outer
 *     payload: {
 *       // user_inventory rows carry an inventory_item_id + the full
 *       // item record.  image_descriptions rows carry only filename +
 *       // description + storage_url — those are RAG hits, not items.
 *       inventory_item_id?: number;
 *       item_name?: string;
 *       item_category?: string;
 *       brand?: string;
 *       location?: string;
 *       storage_url?: string;
 *       // ...other fields we don't need
 *     };
 *   }>;
 *   count: number;
 * }
 *
 * Note: `collection` lives on the row, NOT inside payload — easy to
 * misread from the live response.  Verified by dumping the slice
 * state after a live chat round.
 */
function parseSearchInventory(data: unknown): FounsiItem[] {
  const items = (data as any)?.items;
  if (!Array.isArray(items)) return [];

  const out: FounsiItem[] = [];
  for (const row of items) {
    const p = row?.payload;
    if (!p) continue;
    if (row.collection !== 'user_inventory') continue; // skip RAG hits
    if (typeof p.inventory_item_id !== 'number') continue;
    if (typeof p.item_name !== 'string' || !p.item_name.trim()) continue;

    out.push({
      inventory_item_id: p.inventory_item_id,
      item_name: p.item_name,
      sub_label:
        (typeof p.location === 'string' && p.location.trim()) ||
        (typeof p.item_category === 'string' && p.item_category) ||
        (typeof p.brand === 'string' && p.brand) ||
        undefined,
      source_image_url:
        typeof p.storage_url === 'string' ? p.storage_url : undefined,
      score: typeof row.score === 'number' ? row.score : undefined,
    });
  }
  return out;
}

/**
 * `lookup_inventory_by_name`, `lookup_inventory_by_tag`,
 * `lookup_inventory_by_category` — anecdotally these return a similar
 * `{ items: [...] }` shape where each row IS the inventory record
 * (no `payload` envelope).  We don't have a live capture yet, so the
 * adapter handles both:
 *   - rows with `inventory_item_id` directly on them, OR
 *   - rows wrapped in a `payload` like search_inventory.
 *
 * When the BE confirms the shape this adapter shouldn't need to
 * change — but keep the fixture-driven test up to date.
 */
function parseLookupInventory(data: unknown): FounsiItem[] {
  const rows = (data as any)?.items ?? (data as any)?.results ?? data;
  if (!Array.isArray(rows)) return [];
  const out: FounsiItem[] = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    // Unwrap a `payload` envelope if present (mirrors search_inventory).
    const p = (r as any).payload ?? r;
    if (typeof p.inventory_item_id !== 'number') continue;
    if (typeof p.item_name !== 'string') continue;
    out.push({
      inventory_item_id: p.inventory_item_id,
      item_name: p.item_name,
      sub_label:
        (typeof p.location === 'string' && p.location.trim()) ||
        (typeof p.item_category === 'string' && p.item_category) ||
        (typeof p.brand === 'string' && p.brand) ||
        undefined,
      source_image_url:
        typeof p.storage_url === 'string' ? p.storage_url : undefined,
    });
  }
  return out;
}

/** Registry: which tools return inventory-item-shaped results. */
const ITEM_RETURNING_TOOLS: Record<
  string,
  (data: unknown) => FounsiItem[]
> = {
  search_inventory: parseSearchInventory,
  lookup_inventory_by_name: parseLookupInventory,
  lookup_inventory_by_tag: parseLookupInventory,
  lookup_inventory_by_category: parseLookupInventory,
};

/** Dedupe a list by inventory_item_id, keeping the highest-score
 *  entry (vector search returns the same item across multiple
 *  collections with slightly different scores). */
function dedupeById(items: FounsiItem[]): FounsiItem[] {
  const byId = new Map<number, FounsiItem>();
  for (const it of items) {
    const prev = byId.get(it.inventory_item_id);
    if (!prev || (it.score ?? 0) > (prev.score ?? 0)) {
      byId.set(it.inventory_item_id, it);
    }
  }
  return Array.from(byId.values());
}

/** Public entry — returns [] for tools we don't know about and for
 *  failed/errored tool results (where `data` is null). */
export function parseItemsFromToolResult(
  toolName: string,
  data: unknown,
): FounsiItem[] {
  if (data == null) return [];
  const adapter = ITEM_RETURNING_TOOLS[toolName];
  if (!adapter) return [];
  return dedupeById(adapter(data));
}
